/**
 * PATH: packages/ui/src/qa/QaPhotoTile.tsx
 * FILE_ID: FE-QA-PHOTO-TILE-001
 * PURPOSE: One QA photo square with Fit/Fill, Fill pan, select, and delete.
 * LANDMINE: Failed and null src must use the explicit tiles, never bare alt
 *      text. Touch pan is off so a phone can still scroll the page.
 */

import { Camera, X } from 'lucide-react'
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

import { clampSize } from '../components/panelResize'
import type { QaGalleryPhoto, QaPhotoView } from './qaPhotoView'

const PAN_THRESHOLD_PX = 4

export function QaPhotoTile({
  photo,
  view,
  selectEnabled,
  selected,
  onToggleSelected,
  onPan,
  onOpen,
  onDelete,
}: {
  photo: QaGalleryPhoto
  view: QaPhotoView
  selectEnabled: boolean
  selected: boolean
  onToggleSelected: () => void
  onPan: (x: number, y: number) => void
  onOpen?: () => void
  onDelete?: () => void
}) {
  const [failed, setFailed] = useState(false)
  const [retrySrc, setRetrySrc] = useState<string | null>(null)
  const retried = useRef(false)
  const src = retrySrc ?? photo.src
  useEffect(() => {
    retried.current = false
    setRetrySrc(null)
    setFailed(false)
  }, [photo.src])
  const drag = useRef<{
    x: number
    y: number
    panX: number
    panY: number
    w: number
    h: number
    moved: boolean
  } | null>(null)
  const broken = Boolean(src && failed)
  const canPan = !view.fit && Boolean(src) && !failed

  const onPointerDown = (event: ReactPointerEvent<HTMLLIElement>) => {
    if (!canPan || event.button !== 0 || event.pointerType === 'touch') return
    const frame = event.currentTarget.querySelector('[data-testid="qa-photo-frame"]')
    const rect = (frame ?? event.currentTarget).getBoundingClientRect()
    drag.current = {
      x: event.clientX,
      y: event.clientY,
      panX: view.panX,
      panY: view.panY,
      w: rect.width,
      h: rect.height,
      moved: false,
    }
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLLIElement>) => {
    const d = drag.current
    if (!d) return
    const dx = event.clientX - d.x
    const dy = event.clientY - d.y
    if (!d.moved && Math.hypot(dx, dy) < PAN_THRESHOLD_PX) return
    d.moved = true
    onPan(
      clampSize(d.panX - (dx / d.w) * 100, 0, 100),
      clampSize(d.panY - (dy / d.h) * 100, 0, 100),
    )
  }

  const endDrag = (event: ReactPointerEvent<HTMLLIElement>) => {
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId)
    } catch {
      /* jsdom / unsupported */
    }
  }

  return (
    <li
      className="relative select-none"
      style={{ width: view.size }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={(event) => {
        const moved = drag.current?.moved
        drag.current = null
        const remove = event.currentTarget.querySelector('button[aria-label="Remove photo"]')
        if (remove && event.target instanceof Node && remove.contains(event.target)) return
        if (!moved) onOpen?.()
      }}
      onDoubleClick={() => {
        if (canPan) onPan(50, 50)
      }}
    >
      <div
        className="relative"
        style={{ width: view.size, height: view.size }}
        data-testid="qa-photo-frame"
      >
        {src && !broken ? (
          <img
            src={src}
            alt={photo.alt || 'QA photo'}
            loading="lazy"
            draggable={false}
            style={view.fit ? undefined : { objectPosition: `${view.panX}% ${view.panY}%` }}
            className={`h-full w-full rounded-md bg-muted ${view.fit ? 'object-contain' : 'object-cover'} ${canPan ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'}`}
            onError={() => {
              if (!retried.current && photo.src) {
                retried.current = true
                const joiner = photo.src.includes('?') ? '&' : '?'
                setRetrySrc(`${photo.src}${joiner}retry=1`)
                return
              }
              setFailed(true)
            }}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg border border-border bg-secondary px-2 text-center">
            <Camera className="h-5 w-5 text-muted-foreground" aria-hidden />
            <p className="text-[10px] leading-tight text-muted-foreground">
              {broken ? 'Photo could not be displayed' : 'Photo unavailable'}
            </p>
            {broken && photo.src ? (
              <a
                href={photo.src}
                target="_blank"
                rel="noreferrer"
                className="text-[10px] text-primary underline"
                onClick={(event) => event.stopPropagation()}
              >
                Open
              </a>
            ) : null}
          </div>
        )}
        {selectEnabled ? (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelected}
            onClick={(event) => event.stopPropagation()}
            aria-label={`Select ${photo.alt || 'photo'}`}
            data-testid={`qa-photo-select-${photo.id}`}
            className="absolute -left-1.5 -top-1.5 z-10 h-3.5 w-3.5 rounded border-border bg-background"
          />
        ) : null}
        {onDelete ? (
          <button
            type="button"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation()
              onDelete()
            }}
            className="absolute -right-1.5 -top-1.5 z-10 rounded-full border border-border bg-background p-0.5"
            title="Remove photo"
            aria-label="Remove photo"
          >
            <X className="h-3 w-3 text-destructive" aria-hidden />
          </button>
        ) : null}
      </div>
      {photo.caption ? (
        <p className="mt-0.5 w-full max-w-full break-words text-[10px] leading-tight text-muted-foreground">
          {photo.caption}
        </p>
      ) : null}
    </li>
  )
}
